import type { Capability, LeadershipPoint, Profile, ProofPoint } from '@portfolio/shared';

/**
 * Approved profile content. Every statement here is supported by the CV.
 * Do not add metrics, outcomes, clients or links that were not supplied.
 */
export const profile: Profile = {
  name: 'Constantin Crismaru',
  role: 'Senior Frontend Engineer',
  headline: 'I build reliable, accessible, high-performance web applications.',
  location: 'Iasi, Romania',
  email: 'constantincrismaru1998@gmail.com',
  // Not rendered anywhere while `showPhone` is false.
  phone: '+40 746 803 431',
  showPhone: false,
  summary:
    'Senior Frontend Engineer with 20 years of software development experience, specializing in ' +
    'enterprise applications built with React, Next.js, and TypeScript. I work across frontend ' +
    'architecture, design systems, performance optimization, accessibility, and technical ' +
    'leadership of teams of up to 10 engineers.',
  yearsOfExperience: 20,
  maxTeamSize: 10,
  languages: [
    { name: 'Romanian', level: 'Native' },
    { name: 'English', level: 'C1 Advanced' },
  ],
  // Three JPEG widths are generated from the original at build-preparation
  // time and live in `public/images`. The alt text describes the photograph
  // rather than repeating the name, which is already adjacent in the markup.
  photo: {
    src: '/images/constantin-crismaru-800.jpg',
    srcSet: [
      '/images/constantin-crismaru-480.jpg 480w',
      '/images/constantin-crismaru-800.jpg 800w',
      '/images/constantin-crismaru-1200.jpg 1200w',
    ].join(', '),
    sizes: '(min-width: 1024px) 22rem, (min-width: 640px) 20rem, 100vw',
    socialSrc: '/images/constantin-crismaru-1200.jpg',
    width: 1200,
    height: 1600,
    alt: 'Constantin Crismaru working at a laptop on a rooftop terrace at night, with a lit city skyline behind him.',
  },
  links: [
    {
      id: 'email',
      label: 'constantincrismaru1998@gmail.com',
      href: 'mailto:constantincrismaru1998@gmail.com',
      external: false,
    },
    {
      id: 'linkedin',
      label: 'LinkedIn',
      href: 'https://www.linkedin.com/in/constantin-crismaru-583a1682',
      external: true,
    },
  ],
};

export const proofPoints: readonly ProofPoint[] = [
  { id: 'experience', value: '20 years', label: 'in software development' },
  { id: 'leadership', value: 'Up to 10', label: 'engineers led' },
  { id: 'stack', value: 'React · TypeScript · Next.js', label: 'primary specialization' },
  { id: 'domains', value: 'Public safety · Healthcare · SaaS · AI', label: 'delivery domains' },
];

export const capabilities: readonly Capability[] = [
  {
    id: 'architecture',
    title: 'Frontend architecture',
    description:
      'Structuring long-lived applications so that feature teams can move without destabilizing ' +
      'the codebase.',
    points: [
      'Component and module boundaries',
      'State strategy and data fetching',
      'Coding standards and reviews',
    ],
  },
  {
    id: 'design-systems',
    title: 'Design systems and reusable components',
    description:
      'Building typed, documented component libraries that keep product surfaces consistent ' +
      'across teams.',
    points: [
      'TypeScript component libraries',
      'Storybook documentation',
      'Shared patterns across modules',
    ],
  },
  {
    id: 'performance',
    title: 'Performance and real-time interfaces',
    description:
      'Keeping data-heavy and streaming interfaces responsive through measurement and targeted ' +
      'optimization.',
    points: [
      'Code splitting and lazy loading',
      'Bundle and rendering optimization',
      'RxJS backpressure for event streams',
    ],
  },
  {
    id: 'accessibility',
    title: 'Accessibility and engineering leadership',
    description:
      'Treating accessibility as a functional requirement, and growing the engineers who ' +
      'maintain it.',
    points: [
      'Semantic HTML and keyboard operation',
      'WCAG-aligned review practices',
      'Mentoring, estimation, release planning',
    ],
  },
];

export const leadershipPoints: readonly LeadershipPoint[] = [
  {
    id: 'team',
    title: 'Led teams of 8-10 engineers',
    description:
      'Coordinated a frontend team on a contracts management platform, from estimation through ' +
      'release delivery.',
  },
  {
    id: 'architecture-ownership',
    title: 'Frontend architecture ownership',
    description:
      'Owned architecture decisions and component design across React, Next.js, Vue.js and ' +
      'AngularJS codebases.',
  },
  {
    id: 'planning',
    title: 'Estimation and release planning',
    description:
      'Planned scope and coordinated releases with product and backend teams in Agile and Scrum ' +
      'settings.',
  },
  {
    id: 'mentoring',
    title: 'Code review and mentoring',
    description:
      'Mentored engineers through code review and architecture discussions, and established ' +
      'shared frontend development standards.',
  },
];
